import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Etiqueta } from '@/components/ui/etiqueta';
import { cn } from '@/lib/utils';
import { cantidadDe, formatoCantidad, formatoFechaYHora } from '@/lib/utils/formato-precio';
import {
  cantidadOGuion,
  cortoDelDisparo,
  cuantasPropuso,
  cuantasReescribieron,
  duracion,
  productosOGuion,
  quienLaLanzo,
  reescritasEnFrase,
  textoDelCorte,
} from '../../lib/corridas';
import type { EventoDeLaLinea } from '../../types/corridas.types';
import { BarraDeResultados, LeyendaDeResultados } from './barra-de-resultados';

const TEXTO = 'min-w-0 [overflow-wrap:anywhere]';

/**
 * Ronda de arreglos (revisión T8/T9): un recálculo que no terminó se dice con un
 * texto para personas; lo que guardó el servidor va a «Detalle técnico» sólo si
 * dice otra cosa. Son los mismos textos que guarda el servidor
 * (`RECALCULO_FALLO` y `RECALCULO_PERDIDO`, `recalculo.service.ts`).
 */
const RECALCULO_NO_TERMINO = {
  FALLO:
    'No se pudo calcular: no se movió ningún precio. Lo que alcanzó a llegar está en Por decidir.',
  PERDIDA: 'Se dio por perdido: no se movió ningún precio.',
} as const;

/**
 * Ronda de R3: lo que guarda el servidor en una corrida que se dio por perdida al
 * abrir otra (`CORRIDA_PERDIDA`, `registro-de-corridas.service.ts`). La tarjeta
 * ya lo dice con sus palabras: no va a «Detalle técnico».
 */
const CORRIDA_PERDIDA = 'Se dio por perdida: el proceso que la corría se detuvo.';

/**
 * Una corrida o un recálculo, compacto (maqueta: `runHTML`). Tres columnas:
 * qué fue y cuándo; lo que pasó con lo propuesto; cuántas y «Ver productos ⤢».
 */
export function TarjetaDeEvento({
  evento: e,
  dentroDeLaEstrategia = false,
  nombreDeLaParte,
  resaltada = false,
  puedeCorrer = false,
  puedeReintentar,
  onVerProductos,
  onVolverAIntentar,
}: {
  evento: EventoDeLaLinea;
  /** Dentro de la vista de una estrategia: «Parte de esta estrategia», «de esta estrategia». */
  dentroDeLaEstrategia?: boolean;
  /** «Por fechas» con una estrategia elegida: su nombre, para «Parte de «X»». */
  nombreDeLaParte?: string | null;
  resaltada?: boolean;
  puedeCorrer?: boolean;
  /**
   * Revisión final de rama (F3): si la estrategia de la corrida puede volver a
   * correr ahora (la misma condición que «Revisar ahora»: activa y no vencida).
   * Sin la función, se ofrece como antes.
   */
  puedeReintentar?: (estrategiaId: string) => boolean;
  onVerProductos?: (e: EventoDeLaLinea) => void;
  onVolverAIntentar?: (estrategiaId: string) => void;
}) {
  const esRecalculo = e.tipo === 'RECALCULO';
  const titulo = esRecalculo ? 'Recálculo de reglas' : (e.estrategia?.nombre ?? '—');
  const etiqueta = esRecalculo
    ? e.parte
      ? dentroDeLaEstrategia
        ? 'Parte de esta estrategia'
        : nombreDeLaParte
          ? `Parte de «${nombreDeLaParte}»`
          : 'Parte de una estrategia'
      : e.estrategiasDelRecalculo != null
        ? `Abarca ${cantidadDe(e.estrategiasDelRecalculo, 'estrategia', 'estrategias')}`
        : 'Sin el detalle por estrategia'
    : cortoDelDisparo(e.disparo);
  const tardo = duracion(e.cuando, e.terminoEn);
  const meta = `${formatoFechaYHora(e.cuando)} · ${quienLaLanzo(e)}${tardo ? ` · tardó ${tardo}` : ''}`;
  const deQuien = dentroDeLaEstrategia
    ? 'de esta estrategia'
    : nombreDeLaParte
      ? `de «${nombreDeLaParte}»`
      : 'de una estrategia';
  const queHizo = esRecalculo
    ? e.parte
      ? `revisó ${productosOGuion(e.revisados)} ${deQuien} (${cantidadOGuion(e.totalDelRecalculo)} en total) · todo va a Por decidir`
      : `recalculó ${productosOGuion(e.totalDelRecalculo)}${
          e.estrategiasDelRecalculo != null
            ? ` de ${cantidadDe(e.estrategiasDelRecalculo, 'estrategia', 'estrategias')}`
            : ''
        } · todo va a Por decidir`
    : `revisó ${productosOGuion(e.revisados)}${
        (e.quedanParaLaProxima ?? 0) > 0
          ? ` · ${e.quedanParaLaProxima === 1 ? 'queda' : 'quedan'} ${formatoCantidad(e.quedanParaLaProxima!)} para la próxima corrida`
          : ''
      }${
        e.yaEsperaban > 0
          ? ` · ${formatoCantidad(e.yaEsperaban)} ${e.yaEsperaban === 1 ? 'ya esperaba' : 'ya esperaban'} en Por decidir`
          : ''
      }${
        e.yaNoAplicaban > 0
          ? ` · ${formatoCantidad(e.yaNoAplicaban)} ${e.yaNoAplicaban === 1 ? 'ya no aplicaba y se descartó sola' : 'ya no aplicaban y se descartaron solas'}`
          : ''
      }`;
  const c = e.conteos;
  // Ronda final de R4: lo que propuso al correr (si otro lo reescribió, `c` cae).
  const propuso = cuantasPropuso(e);
  // RG3: un recálculo entero, cuántos productos de cada estrategia.
  const deCadaEstrategia =
    esRecalculo && !e.parte && e.porEstrategia && e.porEstrategia.length > 0
      ? `Productos por estrategia: ${e.porEstrategia
          .map((x) => `${x.nombre}: ${formatoCantidad(x.productos)}`)
          .join(' · ')}`
      : null;
  // Lo que propuso al correr y después reescribió otro: se dice, así las tarjetas
  // que suman más que el total del período se entienden. Ronda del QA de R4
  // (R4-35): también las que siguen ligadas a ella (`reescritasDespues`), con la
  // misma frase (antes, «N se volvieron a calcular después»).
  const reescritasPorOtro = cuantasReescribieron(e);
  // Ronda del QA de R4 (R4-6): la cortó un apagado (o una baja): lo propuesto
  // se retiró de Por decidir; nunca «queda en Por decidir».
  const corte = textoDelCorte(e);
  // La estrategia ya no corre (apagada o vencida): no se promete un reintento.
  const vuelveAIntentar = !e.estrategia?.id || !puedeReintentar || puedeReintentar(e.estrategia.id);
  const reintento = vuelveAIntentar
    ? 'La próxima corrida vuelve a intentar.'
    : 'Su estrategia no está corriendo: no vuelve a intentar sola.';
  const noTermino = e.estado === 'FALLO' || e.estado === 'PERDIDA';
  const textoDelRecalculo =
    e.estado === 'PERDIDA' ? RECALCULO_NO_TERMINO.PERDIDA : RECALCULO_NO_TERMINO.FALLO;
  // El crudo, sólo si agrega algo a lo que ya se dijo.
  const yaDicho = esRecalculo ? textoDelRecalculo : CORRIDA_PERDIDA;
  const detalleTecnico = e.error && e.error !== yaDicho ? e.error : null;

  return (
    <article
      id={`evento-${e.id}`}
      data-testid="tarjeta-de-evento"
      data-caja
      aria-label={`${titulo}, ${formatoFechaYHora(e.cuando)}`}
      className={cn(
        'grid gap-4 rounded-tarjeta border bg-card shadow-sm p-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,5fr)_minmax(0,3fr)]',
        resaltada && 'ring-2 ring-primary'
      )}
    >
      <div className={TEXTO}>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h4 className={cn('text-sm font-semibold', TEXTO)}>{titulo}</h4>
          <Etiqueta tono={esRecalculo ? 'info' : 'neutro'} className="whitespace-normal">
            {etiqueta}
          </Etiqueta>
        </div>
        <p className={cn('mt-1 text-xs text-muted-foreground', TEXTO)}>{meta}</p>
        {e.estado !== 'EN_CURSO' && !noTermino && (
          <p className={cn('mt-0.5 text-xs text-muted-foreground', TEXTO)}>{queHizo}</p>
        )}
        {deCadaEstrategia && (
          <p className={cn('mt-0.5 text-xs text-muted-foreground', TEXTO)}>{deCadaEstrategia}</p>
        )}
      </div>

      {e.estado === 'EN_CURSO' ? (
        <div className={cn('flex items-start gap-2 text-sm', TEXTO)}>
          <Loader2
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary"
          />
          <div className="min-w-0 flex-1">
            {esRecalculo
              ? 'Recálculo en curso…'
              : `Corrida en curso… revisó ${cantidadOGuion(e.revisados)} de ${cantidadDe(e.aRevisar, 'producto', 'productos')}`}
            {!esRecalculo && e.aRevisar > 0 && (
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary"
                  style={{ width: `${Math.min(100, ((e.revisados ?? 0) / e.aRevisar) * 100)}%` }}
                />
              </div>
            )}
          </div>
        </div>
      ) : noTermino ? (
        <div className={cn('space-y-1 text-sm', TEXTO)}>
          <Etiqueta tono="bloqueo">No terminó</Etiqueta>
          <p className={TEXTO}>
            {esRecalculo
              ? textoDelRecalculo
              : e.estado === 'PERDIDA'
                ? `Se perdió: el proceso que la corría se detuvo después de revisar ${productosOGuion(e.revisados)}. ${corte ?? 'Lo que ya había propuesto queda en Por decidir.'} ${reintento}`
                : `Se cortó por un error después de revisar ${productosOGuion(e.revisados)}. ${corte ?? 'Lo que ya había propuesto queda en Por decidir.'} ${reintento}`}
          </p>
          {detalleTecnico && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Detalle técnico</summary>
              <p className={cn('mt-1', TEXTO)}>{detalleTecnico}</p>
            </details>
          )}
        </div>
      ) : propuso > 0 ? (
        <div className={cn('space-y-1.5', TEXTO)}>
          {c.propuestas > 0 && (
            <>
              <BarraDeResultados conteos={c} />
              <LeyendaDeResultados conteos={c} />
            </>
          )}
          {c.limitadas > 0 && (
            <p className="text-xs text-baja">
              {formatoCantidad(c.limitadas)}{' '}
              {c.limitadas === 1 ? 'la limitó una regla' : 'las limitó una regla'}
            </p>
          )}
          {reescritasPorOtro > 0 && (
            <p className="text-xs text-muted-foreground">
              Propuso {formatoCantidad(propuso)} · {reescritasEnFrase(propuso, reescritasPorOtro)}
            </p>
          )}
          {corte && <p className="text-xs text-muted-foreground">{corte}</p>}
        </div>
      ) : (
        <p className={cn('text-sm text-muted-foreground', TEXTO)}>
          {corte
            ? corte
            : esRecalculo
              ? 'No hizo falta proponer cambios.'
              : 'Revisó todo y no hizo falta proponer cambios.'}
        </p>
      )}

      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-2 lg:flex-col lg:items-end',
          TEXTO
        )}
      >
        {e.estado === 'EN_CURSO' ? (
          <span className="text-xs text-muted-foreground">Se actualiza sola</span>
        ) : noTermino ? (
          !esRecalculo &&
          puedeCorrer &&
          onVolverAIntentar &&
          e.estrategia?.id &&
          (puedeReintentar?.(e.estrategia.id) ?? true) && (
            <Button
              size="sm"
              variant="outline"
              className="h-auto whitespace-normal py-1"
              onClick={() => onVolverAIntentar(e.estrategia!.id!)}
            >
              Volver a intentar
            </Button>
          )
        ) : (
          <>
            <p className="text-right text-xs text-muted-foreground">
              <b className="text-sm font-semibold tabular-nums text-foreground">
                {formatoCantidad(propuso)}
              </b>{' '}
              {propuso === 1 ? 'propuesta' : 'propuestas'}
              {c.propuestas > 0 && (
                <>
                  {' · '}
                  <span className="text-ok">sube {formatoCantidad(c.suben)}</span> /{' '}
                  <span className="text-baja">baja {formatoCantidad(c.bajan)}</span>
                </>
              )}
            </p>
            {/* Ronda del QA de R4 (R4-35): también si otro le reescribió todo. */}
            {propuso > 0 && onVerProductos && (
              <Button
                size="sm"
                variant="outline"
                className="h-auto whitespace-normal py-1"
                onClick={() => onVerProductos(e)}
              >
                Ver productos ⤢
              </Button>
            )}
          </>
        )}
      </div>
    </article>
  );
}
